/**
 * Group(N:M user × group membership)— Beta v0.0.1 新增
 * -----------------------------------------------------------------------------
 * 對齊 docs/40 §2 + §3.3 + §8 + `api-service/app/groups/{router,models}.py`。
 *
 * Group 概念:
 * - 1 user 同時是 N 個 group 的 member;首登自動建 personal group(`is_personal=true`)
 * - shared group 由 owner 建,可加 / 移除 member
 * - 不同 group 看到不同 projects + 各自獨立 wallet(per-(user,group) 配對 200 點/月)
 *
 * v0.0.1 namespace:
 * - `GroupEntity`(camelCase)/ `GroupEntityDto`(snake_case wire)
 * - `GroupMembership` / `GroupMembershipDto`
 *
 * 註:group 側 wallet audit(`groups/{gid}/wallet_events`)型別尚未接 UI,先不在此定義;
 * 待真做 wallet audit 畫面時再加回(與 user 側交易流水共用一份事件 union)。
 */

// ─── 工作區狀態 / 方案 enum(R105 從已移除的 billing.types 搬入;group status 走 /me)──
//
// 金流(billing)整條砍除後(docs/64 合約制),這三個 enum 仍由 `GET /api/me` 的 group
// 欄位使用(工作區狀態 / 方案層),故落在 group domain。`PENDING_ORDER` 是 billing-era
// 遺留值(合約制無訂單 → 理論上不再出現;保留字面向後相容,BE 漂移時只改這裡)。
export type WorkspaceStatus = 'PENDING_ORDER' | 'ACTIVE' | 'SUSPENDED';
export type PlanKind = 'personal' | 'team_seat';
export type PlanTier = 'plus' | 'pro' | 'max';

// ─── DTOs (snake_case wire format) ───────────────────────────────────────────

export interface GroupEntityDto {
  gid: string;
  owner_uid: string;
  is_personal: boolean;
  name: string;
  description: string;
  member_count: number;
  created_at: string;
  updated_at: string;
  /**
   * R73 billing 擴充 4 欄(docs/46 §2.2.1 E0;F0 凍結)。
   * 全部 optional — BE 未升級前不回這四欄,mapper 端 normalize 補預設
   * (`status="ACTIVE"` / `plan_kind=null` / `plan_tier=null` / `seat_limit=1`)
   * 向後相容。**沒有舊 draft 的 boolean active 欄位**(已作廢,OQ-1 拍 status enum)。
   */
  status?: WorkspaceStatus;
  /** 試玩 group 回 null。 */
  plan_kind?: PlanKind | null;
  plan_tier?: PlanTier | null;
  /** personal 試玩 = 預設值 1(BE GroupEntity.seat_limit 預設;不可空)。 */
  seat_limit?: number;
}

export type GroupMembershipRole = 'owner' | 'member';
// T3(docs/50)— BE `GET /groups/{gid}/members` 可回 `REMOVED`(軟移除終態);FE 通常只顯 ACTIVE。
export type GroupMembershipStatus = 'ACTIVE' | 'REMOVING' | 'REMOVED';

export interface GroupMembershipDto {
  member_cuid: string;
  role: GroupMembershipRole;
  status: GroupMembershipStatus;
  invited_by?: string | null;
  invited_at?: string | null;
  accepted_at?: string | null;
}

// ─── Request payloads ────────────────────────────────────────────────────────

/* `GroupCreateRequestDto` 已移除(2026-08-10)—— `POST /api/groups` BE 2026-06-23 下架,
   FE 建立工作區整條鏈同輪拆除(合約制:工作區由管理員建合約開通)。 */

export interface GroupUpdateRequestDto {
  name?: string | null;
  description?: string | null;
}

export interface AddMemberRequestDto {
  cuid: string;
  role: 'member';
}

// ─── camelCase domain types ──────────────────────────────────────────────────

export interface GroupEntity {
  gid: string;
  ownerUid: string;
  isPersonal: boolean;
  name: string;
  description: string;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
  /**
   * R73 billing 4 欄(docs/46 §2.2.1;F0)— domain 端 **required**(mapper 邊界
   * normalize 後不再有缺值),UI 不需逐處 `??` 防呆。
   *
   * 工作區狀態判定一律走 selector(`isWorkspaceActive` / `isDefaultTrialWorkspace`),
   * **禁止**散落裸比對 `g.status === 'ACTIVE'`。
   */
  status: WorkspaceStatus;
  /** 試玩 group = null。 */
  planKind: PlanKind | null;
  planTier: PlanTier | null;
  seatLimit: number;
}

export interface GroupMembership {
  memberCuid: string;
  role: GroupMembershipRole;
  status: GroupMembershipStatus;
  invitedBy: string | null;
  invitedAt: string | null;
  acceptedAt: string | null;
}

// ─── DTO → camelCase mappers ─────────────────────────────────────────────────

export function mapGroupEntityDto(dto: GroupEntityDto): GroupEntity {
  return {
    gid: dto.gid,
    ownerUid: dto.owner_uid,
    isPersonal: dto.is_personal,
    name: dto.name,
    description: dto.description,
    memberCount: dto.member_count,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    // R73 F0 — mapper 邊界 normalize(對齊 docs/46 §2.2.1「既有 doc 無這四欄 →
    // 預設 status="ACTIVE" / seat_limit=1 向後相容」;BE 漂 null 也吃得下)
    status: dto.status ?? 'ACTIVE',
    planKind: dto.plan_kind ?? null,
    planTier: dto.plan_tier ?? null,
    seatLimit: dto.seat_limit ?? 1,
  };
}

export function mapGroupMembershipDto(dto: GroupMembershipDto): GroupMembership {
  return {
    memberCuid: dto.member_cuid,
    role: dto.role,
    status: dto.status,
    invitedBy: dto.invited_by ?? null,
    invitedAt: dto.invited_at ?? null,
    acceptedAt: dto.accepted_at ?? null,
  };
}

// ─── Billing selectors(R73;docs/46 §2.2.1 + §5.1)─────────────────────────────

/**
 * 試玩工作區判定(首登自動建的 0 點 personal group)。
 *
 * 試玩判定**靠 `planKind == null`**(不靠 status / balance):付費後的 personal
 * 訂閱 `planKind === 'personal'` 不再是試玩。0 點是試玩的**預期狀態**,UI 不用紅色。
 */
export function isDefaultTrialWorkspace(
  g: Pick<GroupEntity, 'isPersonal' | 'planKind'>,
): boolean {
  return g.isPersonal && g.planKind == null;
}

/**
 * 工作區是否已啟用(可消費 / 可 switchGroup 進入)。
 *
 * 全 repo **禁止**裸比對 `g.status === 'ACTIVE'`,一律走本 selector — enum 字面
 * 集中一處,F6 漂移時只改這裡。`PENDING_ORDER`(待付款)/ `SUSPENDED`(停用)
 * 都不是 active;消費撞 BE 會回 403 `WORKSPACE_NOT_ACTIVE`。
 */
export function isWorkspaceActive(g: Pick<GroupEntity, 'status'>): boolean {
  return g.status === 'ACTIVE';
}

/**
 * R106 — 合約制工作區判定(docs/65:合約開通的工作區 `plan_kind === 'team_seat'`)。
 *
 * 用途:成員管理路徑分流(docs/65 §③)。合約工作區成員 SSOT = contract seats,owner 一律走
 * `/contracts/mine`(email);舊 `/groups/{gid}/members`(cuid)對合約工作區**降唯讀**——
 * 用它加人會產生 cron 管不到的 orphan membership。非合約的一般 team group(`planKind == null`
 * 且非 personal)仍走舊 cuid 成員 API。
 */
export function isContractWorkspace(g: Pick<GroupEntity, 'planKind'>): boolean {
  return g.planKind === 'team_seat';
}
